import type { Rect } from "../types/C1_AppTypes";
import type { WallSide } from "../types/C1_ModelTypes";
import type { C1_InteractiveEloPlacement } from "../types/C1_EloTypes";
import { resolveFurnitureWallDocking } from "../furniture/C1_furnitureDocking";
import { clampToRoomPolygon, type RoomPlacementGeometry } from "../furniture/C1_roomPlacementGeometry";

/** Reuses furniture snapping, including hidden walls and the modifier-key escape. */
export function moveInteractiveEloPlacement(
  placement: C1_InteractiveEloPlacement,
  point: { x: number; y: number },
  rect: Rect,
  hiddenWalls: readonly WallSide[],
  disabled = false,
  geometry?: RoomPlacementGeometry,
): C1_InteractiveEloPlacement {
  const clamped = geometry ? clampToRoomPolygon(point, geometry) : {
    x: Math.min(rect.x2, Math.max(rect.x1, point.x)),
    y: Math.min(rect.y2, Math.max(rect.y1, point.y)),
  };
  const docking = resolveFurnitureWallDocking(clamped, geometry ?? rect, {
    wallDockable: true, autoRotate: true, autoReflect: false, excludedSides: hiddenWalls, disabled,
  });
  return {
    paletteId: placement.paletteId,
    relativePosition: {
      x: (docking.position.x - rect.x1) / Math.max(0.001, rect.x2 - rect.x1),
      y: (docking.position.y - rect.y1) / Math.max(0.001, rect.y2 - rect.y1),
    },
    side: docking.placement?.side,
    wallDocked: docking.placement?.wallDocked,
    _wallId: docking.placement?.wallId,
    rotation: docking.placement?.rotation ?? placement.rotation,
    reflect: placement.reflect,
  };
}

/** Keeps independent devices aligned with their room when its dimensions change. */
export function interactiveEloAbsolutePosition(placement: C1_InteractiveEloPlacement, rect: Rect) {
  return {
    x: rect.x1 + placement.relativePosition.x * (rect.x2 - rect.x1),
    y: rect.y1 + placement.relativePosition.y * (rect.y2 - rect.y1),
  };
}

/** Shared by drawing and placement checks; invalid or missing coordinates are unplaced. */
export function resolveInteractiveEloPosition(placement: C1_InteractiveEloPlacement | undefined, rect: Rect | undefined) {
  if (!placement || !rect || !placement.relativePosition) return undefined;
  const { x, y } = placement.relativePosition;
  if (![x, y, rect.x1, rect.y1, rect.x2, rect.y2].every(Number.isFinite)) return undefined;
  return interactiveEloAbsolutePosition(placement, rect);
}
